import { CloudDownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { DateField, Show, useModal, useTable } from "@refinedev/antd";
import {
  CrudFilters,
  HttpError,
  IResourceComponentsProps,
  getDefaultFilter,
  useApiUrl,
  useCustom,
  useTranslate,
} from "@refinedev/core";
import {
  App,
  Button,
  Col,
  Modal,
  Row,
  Segmented,
  Space,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import axiosInstance from "axiosInstance";
import { RangePicker } from "components/datepicker";
import HighlightTimetableStatus from "components/highlightTimetableStatus";
import TextBox from "components/textBox";
import { DATE_FORMAT } from "config";
import {
  IEvaluate,
  ITimeTableReportStudent,
  IUser,
  IUserExtended,
} from "interfaces";
import { downloadData } from "utils/domHelper";
import { formatClassPeriod } from "utils/modelHelper";
const { Title, Text } = Typography;

const DEFAULT_RANGE_DATE_AMOUNT = 30;

export const ReportStudentsShow: React.FC<IResourceComponentsProps> = () => {
  const { id } = useParams();
  const t = useTranslate();
  const { notification } = App.useApp();
  // const { token } = theme.useToken();
  const {
    modalProps: modalDetailProps,
    show: showDetail,
    close: closeTimeTableReport,
  } = useModal();
  const [detail, setDetail] = useState<IUserExtended | null>();
  const [tab, setTab] = useState(1);

  const { tableProps, tableQueryResult, filters, setFilters } = useTable<
    ITimeTableReportStudent,
    HttpError
  >({
    resource: `reports/student/timetable/${id}`,
    syncWithLocation: true,
    queryOptions: {
      enabled: Boolean(id),
    },
    pagination: {
      mode: "server",
    },
    filters: {
      initial: [
        {
          field: "startDate",
          operator: "eq",
          value: dayjs()
            .subtract(DEFAULT_RANGE_DATE_AMOUNT, "days")
            .startOf("days")
            .toISOString(),
        },
        {
          field: "endDate",
          operator: "eq",
          value: dayjs().endOf("days").toISOString(),
        },
      ],
    },
    onSearch: ({ q }: { q: string }) => {
      return [
        {
          field: "q",
          operator: "eq",
          value: q,
        },
      ];
    },
  });
  const {
    tableProps: tableStudents,
    tableQueryResult: resultStudents,
    filters: filterStudents,
    setFilters: setFilterStudents,
  } = useTable<ITimeTableReportStudent, HttpError>({
    resource: `reports/student/assessment/${id}`,
    syncWithLocation: true,
    queryOptions: {
      enabled: Boolean(id),
    },
    pagination: {
      mode: "server",
    },
    filters: {
      initial: [
        {
          field: "startDate",
          operator: "eq",
          value: dayjs()
            .subtract(DEFAULT_RANGE_DATE_AMOUNT, "days")
            .startOf("days")
            .toISOString(),
        },
        {
          field: "endDate",
          operator: "eq",
          value: dayjs().endOf("days").toISOString(),
        },
      ],
    },
  });

  const startDate = useMemo(
    () =>
      getDefaultFilter("startDate", filters, "eq") ||
      getDefaultFilter("startDate", filterStudents, "eq"),
    [filters, filterStudents]
  );
  const endDate = useMemo(
    () =>
      getDefaultFilter("endDate", filters, "eq") ||
      getDefaultFilter("endDate", filterStudents, "eq"),
    [filters, filterStudents]
  );
  const handleRangeDateChange = useCallback(
    (startDate: string | null, endDate: string | null) => {
      if (startDate && endDate) {
        const finalFilters: CrudFilters = ([] as CrudFilters)
          .concat({
            field: "startDate",
            operator: "eq",
            value: startDate,
          })
          .concat({ field: "endDate", operator: "eq", value: endDate });

        setFilters(finalFilters);
        setFilterStudents(finalFilters);
      }
    },
    [setFilters, setFilterStudents]
  );

  const isTableLoading =
    tableQueryResult?.isFetching ||
    tableQueryResult?.isRefetching ||
    tableQueryResult?.isLoading;
  const isTableStudentLoading =
    resultStudents?.isFetching ||
    resultStudents?.isRefetching ||
    resultStudents?.isLoading;

  const apiUrl = useApiUrl();
  const { data: userData, isLoading: isUserLoading } = useCustom<IUserExtended>(
    {
      url: `${apiUrl}/reports/student/info/${id}`,
      method: "get",
      config: {
        query: {
          page: 0,
          size: 1000000,
          orderBy: "-startTime",
        },
      },
    }
  );
  const userRecord = userData?.data;

  const { data: totalData, isLoading: isTotalAssessmentDataLoading } =
    useCustom({
      url: `${apiUrl}/reports/student/assessment/point/${id}`,
      method: "get",
      config: {
        query: {
          endDate: endDate || dayjs().endOf("days").toISOString(),
          startDate:
            startDate ||
            dayjs()
              .subtract(DEFAULT_RANGE_DATE_AMOUNT, "days")
              .startOf("days")
              .toISOString(),
        },
      },
    });
  const totalAssessmentData = totalData?.data;

  const [isRefetchingExport, setIsRefetchingExport] = useState(false);

  const refetchExport = async () => {
    try {
      setIsRefetchingExport(true);
      const { data } = await axiosInstance.get(
        `${apiUrl}/reports/student/export/${id}`,
        {
          responseType: "blob",
        }
      );
      downloadData(data, t("reports_students.exports.fileName"));
      notification.success({
        message: t("reports_students.notifications.exportSuccess"),
      });
    } catch (error: any) {
      notification.error({
        message:
          error?.message ?? t("reports_students.notifications.exportError"),
      });
    } finally {
      setIsRefetchingExport(false);
    }
  };
  const refetchAssessmentExport = async () => {
    try {
      setIsRefetchingExport(true);
      const { data } = await axiosInstance.get(
        `${apiUrl}/reports/student/assessment/export/${id}`,
        {
          responseType: "blob",
        }
      );
      downloadData(data, t("reports_students.exports.fileNameAss"));
      notification.success({
        message: t("reports_students.notifications.exportSuccess"),
      });
    } catch (error: any) {
      notification.error({
        message:
          error?.message ?? t("reports_students.notifications.exportError"),
      });
    } finally {
      setIsRefetchingExport(false);
    }
  };

  const sumTotal = useCallback(() => {
    if (detail?.studentAssessments) {
      // return detail?.studentAssessments  sumBy;
      return detail?.studentAssessments.reduce(function (acc, obj) {
        return acc + obj?.point;
      }, 0);
    }
  }, [detail]);
  const sumAdd = useCallback(() => {
    if (detail?.studentAssessments) {
      // return detail?.studentAssessments  sumBy;
      return detail?.studentAssessments.reduce(function (acc, obj) {
        return obj?.point > 0 ? acc + obj?.point : acc;
      }, 0);
    }
  }, [detail]);
  const sumMinus = useCallback(() => {
    if (detail?.studentAssessments) {
      // return detail?.studentAssessments  sumBy;
      return detail?.studentAssessments.reduce(function (acc, obj) {
        return obj?.point < 0 ? acc + obj?.point : acc;
      }, 0);
    }
  }, [detail]);

  return (
    <>
      <Show
        isLoading={
          isTableLoading ||
          isUserLoading ||
          isTableStudentLoading ||
          isTotalAssessmentDataLoading
        }
        headerButtons={[
          <Button
            key="download"
            icon={<CloudDownloadOutlined />}
            loading={isRefetchingExport}
            disabled={isRefetchingExport}
            onClick={() => refetchExport()}
          >
            {t("reports_students.exports.btnAss")}
          </Button>,
          <Button
            key="download"
            icon={<CloudDownloadOutlined />}
            loading={isRefetchingExport}
            disabled={isRefetchingExport}
            onClick={() => refetchAssessmentExport()}
          >
            {t("reports_students.exports.btnStudent")}
          </Button>,
        ]}
      >
        <Row gutter={[16, 24]} style={{ marginBottom: 32 }}>
          <Col xs={24} md={12} lg={8}>
            <TextBox
              value={userRecord?.fullName ?? "--"}
              label={t("users.fields.fullName")}
            />
          </Col>

          <Col xs={24} md={12} lg={8}>
            <TextBox
              value={userRecord?.username ?? "--"}
              label={t("users.fields.username")}
            />
          </Col>

          <Col xs={24} md={12} lg={8}>
            <TextBox
              value={userRecord?.mobile ?? "--"}
              label={t("users.fields.mobile")}
            />
          </Col>

          <Col xs={24} md={12} lg={8}>
            <TextBox
              value={userRecord?.email ?? "--"}
              label={t("users.fields.email")}
            />
          </Col>

          <Col xs={24} md={12} lg={8}>
            <TextBox
              value={userRecord?.position ?? "--"}
              label={t("users.fields.position")}
            />
          </Col>

          <Col xs={24} md={12} lg={8}>
            <TextBox
              value={userRecord?.teacher ?? "--"}
              label={t("users.fields.teacher")}
            />
          </Col>
        </Row>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <Segmented
            defaultValue={1}
            options={[
              {
                label: t("reports_students.tab.1"),
                value: 1,
              },
              {
                label: t("reports_students.tab.2"),
                value: 2,
              },
            ]}
            onChange={(value) => {
              setTab(+value);
            }}
          />
          <RangePicker
            allowClear={false}
            format={DATE_FORMAT}
            value={[
              startDate ? dayjs(startDate) : null,
              endDate ? dayjs(endDate) : null,
            ]}
            onCalendarChange={(rangeValue) => {
              const startDate = rangeValue?.[0]?.toISOString() ?? null;
              const endDate = rangeValue?.[1]?.toISOString() ?? null;
              handleRangeDateChange(startDate, endDate);
            }}
          />
        </div>
        {tab === 1 ? (
          <Table {...tableProps} rowKey="idx" scroll={{ x: "100%", y: "auto" }}>
            <Table.Column
              dataIndex="idx"
              title={t("reports_students.details.fields.idx")}
              width={70}
            />
            <Table.Column
              dataIndex="date"
              title={t("reports_students.details.fields.date")}
              width={150}
            />
            <Table.Column
              dataIndex="className"
              title={t("reports_students.fields.class")}
              render={(v, r: any) => r.responseClass.className}
              width={150}
            />
            <Table.Column
              dataIndex="subjectName"
              title={t("reports_students.fields.subject")}
              render={(v, r: any) => r.subject?.name}
              width={150}
            />
            <Table.Column
              dataIndex="subjectCode"
              title={t("reports_students.fields.subjectCode")}
              render={(v, r: any) => r.subject?.code}
              width={150}
            />
            <Table.Column
              dataIndex="responseClass"
              title={t("reports_students.fields.typeClass")}
              render={(responseClass) =>
                t(`users.details.fields.classType_${responseClass.classType}`)
              }
              width={150}
            />
            <Table.Column
              dataIndex="classPeriod"
              title={t("reports_students.details.fields.classPeriod")}
              render={(classPeriod) => formatClassPeriod(classPeriod)}
              width={250}
            />
            <Table.Column
              dataIndex="attendanceStatus"
              title={t(
                "reports_students.details.fields.currentAttendanceStatus"
              )}
              render={(status) => (
                <HighlightTimetableStatus
                  field="currentAttendanceStatus_"
                  status={status}
                />
              )}
              width={220}
            />
            <Table.Column
              dataIndex="additionPoint"
              title={t("reports_students.fields.plusPoints")}
              width={150}
              align="center"
              render={(v) => v && `+${v}`}
            />
            <Table.Column
              dataIndex="minusPoint"
              title={t("reports_students.fields.minusPoint")}
              width={150}
              align="center"
            />
            <Table.Column
              title={t("table.actions")}
              dataIndex="actions"
              width={120}
              render={(_, record: IUserExtended) => {
                return (
                  <Space>
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => {
                        showDetail();
                        setDetail(record);
                      }}
                    />
                  </Space>
                );
              }}
            />
          </Table>
        ) : (
          <Table
            {...tableStudents}
            rowKey="idx"
            scroll={{ x: "100%", y: "auto" }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell
                    index={0}
                    colSpan={2}
                  ></Table.Summary.Cell>
                  <Table.Summary.Cell index={1} colSpan={1} align="center">
                    <Text strong>
                      {t("reports_students.fields.totalPoint")}:
                    </Text>{" "}
                    <Text
                      style={{
                        color:
                          totalAssessmentData?.totalPoint > 0
                            ? `#0084FF`
                            : `#FF0000`,
                      }}
                    >
                      {totalAssessmentData?.totalPoint}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} colSpan={1} align="center">
                    <Text strong>
                      {t("reports_students.fields.totalAddPoint")}:
                    </Text>{" "}
                    <Text
                      style={{
                        color:
                          totalAssessmentData?.additionPoint > 0
                            ? `#0084FF`
                            : `#FF0000`,
                      }}
                    >
                      {totalAssessmentData?.additionPoint}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} colSpan={1} align="center">
                    <Text strong>
                      {t("reports_students.fields.totalMinusPoint")}:
                    </Text>{" "}
                    <Text
                      style={{
                        color:
                          totalAssessmentData?.minusPoint > 0
                            ? `#0084FF`
                            : `#FF0000`,
                      }}
                    >
                      {totalAssessmentData?.minusPoint}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell
                    index={4}
                    colSpan={1}
                  ></Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          >
            <Table.Column
              dataIndex="idx"
              title={t("reports_students.details.fields.idx")}
              width={70}
            />
            <Table.Column
              dataIndex="date"
              title="Thời gian"
              // title={t("reports_students.details.fields.date")}
              width={150}
            />
            {/* <Table.Column
              dataIndex="subjectName"
              title={t("reports_students.fields.subject")}
              render={(v, r: any) => r.subject?.name}
              width={150}
            />
            <Table.Column
              dataIndex="subjectCode"
              title={t("reports_students.fields.subjectCode")}
              render={(v, r: any) => r.subject?.code}
              width={150}
            /> */}

            <Table.Column
              dataIndex="totalPoint"
              title={t("reports_students.fields.totalPoint")}
              align="center"
              width={150}
              render={(v) => (v > 0 ? `+${v}` : v)}
            />
            <Table.Column
              dataIndex="additionPoint"
              title={t("reports_students.fields.plusPoints")}
              width={150}
              align="center"
              render={(v) => v && `+${v}`}
            />
            <Table.Column
              dataIndex="minusPoint"
              title={t("reports_students.fields.minusPoint")}
              width={150}
              align="center"
            />
            <Table.Column
              title={t("table.actions")}
              dataIndex="actions"
              width={80}
              render={(_, record: IUserExtended) => {
                return (
                  <Space>
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => {
                        showDetail();
                        setDetail(record);
                      }}
                    />
                  </Space>
                );
              }}
            />
          </Table>
        )}
      </Show>
      <Modal
        {...modalDetailProps}
        width={tab === 1 ? 700 : 1000}
        title={t("reports_students.titles.details")}
        onCancel={() => {
          setDetail(null);
          closeTimeTableReport();
        }}
        footer={null}
      >
        {tab === 1 ? (
          <Table
            rowKey="id"
            dataSource={detail?.studentEvaluates || []}
            scroll={{ x: 300 }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <Title level={5}>
                      {t("reports_students.fields.total")}
                    </Title>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} colSpan={2}>
                    <Text>
                      {(detail?.additionPoint || 0) + (detail?.minusPoint || 0)}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          >
            <Table.Column<IUser>
              dataIndex="key"
              key="key"
              width={50}
              title={t("reports_students.fields.idx")}
              render={(_, record, index) => {
                return index + 1;
              }}
            />
            <Table.Column<IEvaluate>
              dataIndex={"evaluate"}
              key="evaluate"
              title={t("reports_students.fields.rewardsViolations")}
              render={(v, r) => r.evaluate.name}
            />
            <Table.Column
              dataIndex={"point"}
              key="point"
              title={t("reports_students.fields.points")}
              render={(v, r: any) => (
                <span
                  style={{
                    color:
                      r.evaluate.type === "ADDITIONAL_POINT"
                        ? `#0084FF`
                        : `#FF0000`,
                  }}
                >
                  {r.evaluate.type === "ADDITIONAL_POINT" ? `+${v}` : `${v}`}
                </span>
              )}
            />

            <Table.Column
              dataIndex={"updatedAt"}
              key="updatedAt"
              title={t("reports_students.fields.time")}
              render={(value: any) => <DateField value={value} />}
            />
          </Table>
        ) : (
          <Table
            rowKey="id"
            dataSource={detail?.studentAssessments || []}
            scroll={{ x: 300 }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <Text strong>
                      {t("reports_students.fields.totalPoint")}:
                    </Text>{" "}
                    <Text>{sumTotal()}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} colSpan={2}>
                    <Text strong>
                      {t("reports_students.fields.totalAddPoint")}:
                    </Text>{" "}
                    <Text>{sumAdd()}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} colSpan={2}>
                    <Text strong>
                      {t("reports_students.fields.totalMinusPoint")}:
                    </Text>{" "}
                    <Text>{sumMinus()}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          >
            <Table.Column<IUser>
              dataIndex="key"
              key="key"
              width={50}
              title={t("reports_students.fields.idx")}
              render={(_, record, index) => {
                return index + 1;
              }}
            />
            <Table.Column
              dataIndex={"updatedAt"}
              key="updatedAt"
              width={170}
              title={t("reports_students.fields.time")}
              render={(value: any) => (
                <DateField value={value} format="HH:mm DD/MM/YYYY" />
              )}
            />
            <Table.Column
              dataIndex={"supervisor"}
              key="supervisor"
              width={250}
              title={t("reports_students.fields.evaluator")}
              render={(v: any) => v?.fullName}
            />
            <Table.Column<IEvaluate>
              dataIndex={"subjectName"}
              key="subjectName"
              width={150}
              title={t("reports_students.fields.subject")}
              render={(v: any) => detail?.subject?.name}
            />
            <Table.Column<IEvaluate>
              dataIndex={"evaluate"}
              key="evaluate"
              title={t("reports_students.fields.rewardsViolations")}
              render={(v: any) => v?.description}
            />
            <Table.Column
              dataIndex={"point"}
              key="point"
              title={t("reports_students.fields.points")}
              render={(v, r: any) => (
                <span
                  style={{
                    color:
                      r.evaluate.type === "ADDITIONAL_POINT"
                        ? `#0084FF`
                        : `#FF0000`,
                  }}
                >
                  {r.evaluate.type === "ADDITIONAL_POINT" ? `+${v}` : `${v}`}
                </span>
              )}
            />
          </Table>
        )}
      </Modal>
    </>
  );
};
